export type BlogVideo =
  | { kind: "youtube"; videoId: string; startSeconds?: number }
  | { kind: "file"; src: string };

const YOUTUBE_ID = /^[\w-]{11}$/;

export function parseBlogVideo(input: string): BlogVideo | null {
  const value = input.trim();
  if (!value) return null;

  if (/^\/[^\s]+$/.test(value) && /\.(mp4|webm|ogg)(\?|#|$)/i.test(value)) {
    return { kind: "file", src: value };
  }

  const youtube = parseYouTubeUrl(value);
  if (youtube) return youtube;

  return null;
}

function parseYouTubeUrl(input: string): BlogVideo | null {
  let url: URL;
  try {
    url = new URL(input.includes("://") ? input : `https://${input}`);
  } catch {
    return YOUTUBE_ID.test(input) ? { kind: "youtube", videoId: input } : null;
  }

  const host = url.hostname.replace(/^www\./, "");
  let videoId: string | null = null;

  if (host === "youtu.be") {
    videoId = url.pathname.split("/").filter(Boolean)[0] ?? null;
  } else if (host === "youtube.com" || host === "m.youtube.com" || host === "youtube-nocookie.com") {
    if (url.pathname.startsWith("/embed/")) {
      videoId = url.pathname.split("/")[2] ?? null;
    } else {
      videoId = url.searchParams.get("v");
    }
  }

  if (!videoId || !YOUTUBE_ID.test(videoId)) return null;

  const startSeconds = parseYouTubeStart(url.searchParams.get("t"));
  return startSeconds !== undefined
    ? { kind: "youtube", videoId, startSeconds }
    : { kind: "youtube", videoId };
}

function parseYouTubeStart(value: string | null): number | undefined {
  if (!value) return undefined;
  if (/^\d+$/.test(value)) return Number(value);
  const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i.exec(value);
  if (!match || !match[0]) return undefined;
  const hours = Number(match[1] || 0);
  const minutes = Number(match[2] || 0);
  const seconds = Number(match[3] || 0);
  const total = hours * 3600 + minutes * 60 + seconds;
  return total > 0 ? total : undefined;
}
